import { useState, useEffect, useRef } from "react";
import {
  InfoCircleOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  Layout,
  Card,
  Button,
  Form,
  Input,
  Table,
  Tabs,
  Modal,
  Spin,
  Badge,
  Row,
  Col,
  Typography,
  Space,
  Breadcrumb,
  Select,
  message,
  Carousel,
} from "antd";
import { useSelector } from "react-redux";
import {
  getExamMethodInformation,
  submitAdultsExamResult,
} from "../../../../../../components/https/age-assessment-apis";
import {
  errorRequestHandel,
  getCustomTitleForGuidlineField,
  johansonGustafsonFormFieldsCombinations,
  johansonGustafsonFormFieldsMapping,
  maplesFormFieldsCombinations,
  ageAssessmentBasePath,
  collationFormFieldsCombinations,
} from "../../../../../../helper";
import Method from "../../child/method/Method";
import { dentalIcons } from "../../../../../../dentail-indetifcation/utils-icons/dentalIcons";
import CustomPrimaryButton from "../../../../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import CustomButtonWithIcon from "../../../../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import { assessmentIcons } from "../../../../../utils/assessmentIcons";
import CustomCardWithHeader from "../../../../../../components/custom-card-with-header/CustomCardWithHeader";
import {
  checkAllowedValues,
  validateNumericValue,
} from "../../../../../../utils/validators/methods/shared";
import { useNavigate } from "react-router-dom";
import ToothOrRadiographTab from "../../tooth-or-radiograph/ToothOrRadiographTab";
import PhysicalRadioGraph from "../../child/physical-radio-graph/PhysicalRadioGraph";
import { useSubscriptionGuard } from "../../../../../../hooks/subscription-hook/UseSubscriptionGuard";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// Mock API constants

export default function SectionedToothAnalysis() {
  const navigate = useNavigate();
  useSubscriptionGuard();

  const radioGraphContainerRef = useRef(null);
  const imageContainerRef = useRef(null);
  const [selectedAtiveTab, setSelectedActiveTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRadioGraphFullscreen, setIsRadioGraphFullscreen] = useState(false);
  const AVAILABLE_METHODS = useSelector(
    (state) => state.ageAssessmentCase?.exameMethods || []
  );
  const currentCaseDetail = useSelector(
    (state) => state.ageAssessmentCase?.currentCaseDetail || null
  );
  let additional_images = currentCaseDetail?.additional_images || [];
  if (currentCaseDetail?.opg && currentCaseDetail.opg.trim() !== "") {
    additional_images = [currentCaseDetail.opg, ...additional_images]; // Insert at index 0
  }
  const { metaData } = useSelector((state) => state.general);
  // State for method selection
  const [selectedMethods, setSelectedMethods] = useState([10]); // Default to Cameriere method
  const [selectedMethod, setSelectedMethod] = useState(AVAILABLE_METHODS[0]);
  const [activeTableMethod, setActiveTableMethod] = useState(
    AVAILABLE_METHODS[0]
  ); // Separate state for table view
  const [selectedSingleMethod, setSelectedSingleMethod] = useState("");

  // State for teeth data
  const [methodsData, setMethodsData] = useState({});
  const [examMethodData, setExamMethodData] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // State for tooth selection and form data
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [selectedTeeth, setSelectedTeeth] = useState({}); // Store selected teeth by type

  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    johanson_gustafson: {},
    gustafson_maples: {},
    collation: {},
  });

  const scoringFields = [
    "attrition_A",
    "secondary_dentin_S",
    "paradentosis_P",
    "root_transparency_T",
    "cementum_C",
    "root_resorption_R",
  ];

  // State for modal
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoType, setInfoType] = useState("");

  const metaDataTeeth = metaData.teeth.map(({ surfaces, id, iso, ...rest }) => {
    // Find a matching tooth in methodsData by iso
    const matchingTooth = methodsData[selectedMethod.slug]?.teeth.find(
      (tooth) => tooth.iso === iso
    );
    return {
      ...rest,
      iso,
      id: matchingTooth ? matchingTooth.id : id, // Replace id if match is found
    };
  });

  const firstQuadrant = metaDataTeeth.slice(0, 8); // Teeth 18-11
  const secondQuadrant = metaDataTeeth.slice(8, 16); // Teeth 21-28
  const thirdQuadrant = metaDataTeeth.slice(24, 32).reverse(); // Teeth 48-41
  const fourthQuadrant = metaDataTeeth.slice(16, 24).reverse(); // Teeth 31-38

  const [selectedTab, setSelectedTab] = useState(0);

  const getTeethList = (data) => {
    return data.flatMap((tooth) =>
      tooth.children.length > 0
        ? tooth.children.map((child) => ({
            id: Number(tooth.id),
            name: child.name,
            iso: child.fdi,
          }))
        : [
            {
              id: Number(tooth.id),
              name: tooth.name,
              iso: tooth.tooth_identifier,
            },
          ]
    );
  };

  // Fetch method data
  const fetchMethodData = async (methodSlug) => {
    setLoading(true);
    try {
      const response = await getExamMethodInformation({
        slug: methodSlug === "collation" ? "johanson_gustafson" : methodSlug,
      });
      if (response.status === 200) {
        let dataObj = response?.data?.data;
        dataObj["teeth"] = getTeethList(dataObj.teeth);
        setMethodsData((prev) => ({
          ...prev,
          [methodSlug]: response?.data?.data,
        }));
        setExamMethodData(response?.data?.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      const defaultMethod = AVAILABLE_METHODS[0];
      if (defaultMethod) {
        await handleMethodToggle(defaultMethod.id);
      }
    };

    fetchInitialData();
  }, []);

  // Check if a method has any saved teeth
  const hasMethodData = (methodSlug) => {
    return Object.keys(formData[methodSlug] || {}).length > 0;
  };

  // Handle method deletion (remove all data for a method)
  const handleDeleteMethod = (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    // Clear all data for this method
    setFormData((prev) => ({
      ...prev,
      [method.slug]: {},
    }));

    // If this was the selected method, switch to another method
    if (selectedMethod.id === methodId && selectedMethods.length > 1) {
      const nextMethodId = selectedMethods.find((id) => id !== methodId);
      const nextMethod = AVAILABLE_METHODS.find((m) => m.id === nextMethodId);
      setSelectedMethod(nextMethod);

      // Check cache first
      if (!methodsData[nextMethod.slug]) {
        fetchMethodData(nextMethod.slug);
      } else {
        setStages(methodsData[nextMethod.slug][0].stages); // Use cached data
        setExamMethodData(methodsData[nextMethod.slug]);
      }
    }

    // If this was the active table method, switch to another method
    if (activeTableMethod.id === methodId) {
      const nextMethodId = selectedMethods.find((id) => id !== methodId);
      const nextMethod = AVAILABLE_METHODS.find((m) => m.id === nextMethodId);
      setActiveTableMethod(nextMethod);
    }

    // Remove from selected methods
    setSelectedMethods((prev) => prev.filter((id) => id !== methodId));
  };

  // Handle changing the active method (for tooth selection and form)
  const handleChangeActiveMethod = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method || method.id === selectedMethod.id) return;

    setSelectedMethod(method);

    // Check cache first
    if (!methodsData[method.slug]) {
      await fetchMethodData(method.slug);
    } else {
      setStages(methodsData[method.slug][0].stages); // Use cached data
      setExamMethodData(methodsData[method.slug]);
    }

    // Clear selected tooth when changing methods
    setSelectedTooth(null);
  };

  // Handle changing the active table method (for preview table only)
  const handleChangeTableMethod = (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    // Only update the active table method, not the selected method
    setActiveTableMethod(method);
    handleMethodToggle(methodId);
  };

  // Handle tooth selection
  const handleToothClick = (tooth) => {
    setSelectedTooth(tooth);
  };

  useEffect(() => {
    if (!selectedTooth || !selectedMethod?.slug) return;

    const currentData =
      formData[selectedMethod.slug]?.[selectedTooth.iso] || {};
    let selectedCombination = currentData?.combination;
    let activeTab = 0;

    let comboList =
      selectedMethod?.slug === "johanson_gustafson"
        ? johansonGustafsonFormFieldsCombinations
        : selectedMethod?.slug === "collation"
        ? collationFormFieldsCombinations
        : maplesFormFieldsCombinations;

    if (selectedCombination !== undefined) {
      const isToothType = comboList.some(
        (item) => item.id === selectedCombination && item.type === "tooth"
      );
      activeTab = isToothType ? 0 : 1;
    } else {
      // Assign default combo based on current tab
      const defaultCombo = comboList.find(
        (item) => item.type === (selectedTab === 0 ? "tooth" : "root")
      );
      selectedCombination = defaultCombo?.id;
      handleInputChange("combination", selectedCombination);
      activeTab = defaultCombo?.type === "tooth" ? 0 : 1;
    }

    // If currentData does not exist, set isSaved to false and initialize missing keys
    if (!currentData.tooth) {
      formData[selectedMethod.slug] = {
        ...formData[selectedMethod.slug],
        [selectedTooth.iso]: {
          tooth: {},
          combination: selectedCombination || "",
          isSaved: false,
        },
      };
    }
    if (currentData) {
      setSelectedTab(activeTab);
      form.setFieldsValue(
        formData[selectedMethod.slug][selectedTooth.iso] || {}
      );
    } else {
      setSelectedTab(0);
    }
  }, [selectedTooth]);

  // Handle form input change
  const handleInputChange = (field, value) => {
    if (!selectedTooth) return;
    setFormData((prev) => ({
      ...prev,
      [selectedMethod.slug]: {
        ...prev[selectedMethod.slug],
        [selectedTooth.iso]: {
          ...prev[selectedMethod.slug][selectedTooth.iso],
          tooth: selectedTooth,
          [field]: value,
        },
      },
    }));
  };

  // Save form data
  const handleSave = () => {
    if (!selectedTooth) return;
    form
      .validateFields()
      .then((values) => {
        // All validation passed, update your formData state here
        setFormData((prev) => ({
          ...prev,
          [selectedMethod.slug]: {
            ...prev[selectedMethod.slug],
            [selectedTooth.iso]: {
              ...prev[selectedMethod.slug][selectedTooth.iso],
              tooth: selectedTooth,
              isSaved: true,
              ...values, // Use the validated values from the form
            },
          },
        }));

        // Disable selection of another tooth of the same type
        setSelectedTeeth((prev) => ({
          ...prev,
          [selectedTooth.type]: selectedTooth, // Store selected tooth under its type
        }));
        // Clear selection after save
        setSelectedTooth(null);
        form.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const isToothDisabled = (tooth) => {
    if (!selectedMethod || !formData || !formData[selectedMethod.slug])
      return false; // Ensure method and formData are defined

    // Get the method data from formData for the active method
    const methodTeeth = formData[selectedMethod.slug] || {};

    // Check if there is a selected tooth of the same type
    for (let selectedToothId in methodTeeth) {
      const selectedTooth = methodTeeth[selectedToothId];
      // Skip if not saved
      if (!selectedTooth.isSaved) continue;
      // If a tooth of the same type is selected and the ISO doesn't match, disable it
      if (
        selectedTooth.tooth.id === tooth.id &&
        selectedTooth.tooth.iso !== tooth.iso
      ) {
        return true; // Disable if a different tooth of the same type is selected
      }
    }

    // Default return false if no conditions are met (tooth is not disabled)
    return false;
  };

  const getToothStatus = (toothIso) => {
    const methodTeeth = formData[selectedMethod.slug];
    if (!methodTeeth) return false;

    for (const key in methodTeeth) {
      const toothData = methodTeeth[key];

      const matchesIso = toothData.tooth?.iso === toothIso;
      const hasScoringData = scoringFields.some(
        (field) => toothData[field] !== undefined && toothData[field] !== ""
      );

      if (matchesIso && hasScoringData) {
        return true;
      }
    }

    return false;
  };

  // Delete tooth data
  const handleDelete = (methodSlug, toothId) => {
    setFormData((prev) => {
      const methodData = { ...prev[methodSlug] };
      delete methodData[toothId];

      return {
        ...prev,
        [methodSlug]: methodData,
      };
    });
  };

  // Show info modal
  const showInfoModal = (type) => {
    setInfoType(type);
    setInfoModalVisible(true);
  };

  // Prepare table data for preview
  const getTableData = (methodSlug) => {
    const methodData = formData[methodSlug];
    if (!methodData) return [];

    return Object.entries(methodData)
      .filter(([_, data]) =>
        scoringFields.some((field) => data[field] !== undefined)
      )
      .map(([id, data]) => ({
        key: id,
        iso: data.tooth.iso,
        name: data.tooth.name,
        ...data,
      }));
  };

  // Check if any method has data
  const hasAnyData = Object.keys(formData).some(
    (method) => Object.keys(formData[method]).length > 0
  );

  // Get methods that will be submitted (have at least one tooth saved)
  const getMethodsToSubmit = () => {
    return AVAILABLE_METHODS.filter((method) => hasMethodData(method.slug));
  };

  // Get column definitions based on method
  const getColumns = (methodSlug) => {
    const baseColumns = [
      {
        title: "Tooth Position",
        dataIndex: "",
        key: "iso",
        render: (item) => "ISO " + item.iso,
      },
      {
        title: "Tooth Name",
        dataIndex: "name",
        key: "name",
      },
    ];

    const actionColumn = {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(methodSlug, record.key)}
        />
      ),
    };

    if (
      methodSlug === "johanson_gustafson" ||
      methodSlug === "gustafson_maples" ||
      methodSlug === "collation"
    ) {
      return [
        ...baseColumns,
        {
          title: johansonGustafsonFormFieldsMapping["A"].label,
          dataIndex: "attrition_A",
          key: "attrition_A",
        },
        {
          title: johansonGustafsonFormFieldsMapping["S"].label,
          dataIndex: "secondary_dentin_S",
          key: "secondary_dentin_S",
        },
        {
          title: johansonGustafsonFormFieldsMapping["P"].label,
          dataIndex: "paradentosis_P",
          key: "paradentosis_P",
        },
        {
          title: johansonGustafsonFormFieldsMapping["T"].label,
          dataIndex: "root_transparency_T",
          key: "root_transparency_T",
        },
        {
          title: johansonGustafsonFormFieldsMapping["C"].label,
          dataIndex: "cementum_C",
          key: "cementum_C",
        },

        {
          title: johansonGustafsonFormFieldsMapping["R"].label,
          dataIndex: "root_resorption_R",
          key: "root_resorption_R",
        },
        actionColumn,
      ];
    }

    return [...baseColumns, actionColumn];
  };

  const getVisibleFieldsForCombination = (combinationId) => {
    let comboList =
      selectedMethod?.slug === "johanson_gustafson"
        ? johansonGustafsonFormFieldsCombinations
        : selectedMethod?.slug === "collation"
        ? collationFormFieldsCombinations
        : maplesFormFieldsCombinations;

    const combo = comboList.find((c) => c.id === combinationId);

    if (!combo) return [];

    const name = combo.name; // e.g. "ASPTCR"
    return name.split(""); // [ 'A', 'S', 'P', 'T', 'C', 'R' ]
  };

  // Render form fields based on method
  const renderFormFields = () => {
    if (!selectedTooth || !selectedMethod) return null;

    const currentData = formData[selectedMethod.slug][selectedTooth.iso] || {};

    const visibleFields = getVisibleFieldsForCombination(
      currentData.combination
    );

    if (selectedMethod.slug === "johanson_gustafson") {
      return (
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <Form.Item
                rules={[{ message: "This field is required", required: true }]}
                label={<Space>Select Combination</Space>}
              >
                <Select
                  placeholder="Enter value"
                  value={currentData.combination}
                  onChange={(e) => handleInputChange("combination", e)}
                >
                  {johansonGustafsonFormFieldsCombinations
                    .filter((item) => {
                      if (selectedTab === 0) {
                        return item.type === "tooth";
                      } else {
                        return item.type === "root";
                      }
                    })
                    .map((item, index) => {
                      return (
                        <Select.Option value={item.id} key={index}>
                          {item.name}
                        </Select.Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <>
              {visibleFields.map((code, index) => {
                const field = johansonGustafsonFormFieldsMapping[code];
                if (!field) return null;

                return (
                  <Col md={8} key={index}>
                    <Form.Item
                      name={field.name}
                      rules={[
                        validateNumericValue(field.label),
                        checkAllowedValues(),
                      ]}
                      label={
                        <Space>
                          {field.label}
                          <Button
                            type="text"
                            icon={<InfoCircleOutlined />}
                            onClick={() => showInfoModal(field.name)}
                            style={{ padding: 0, margin: 0 }}
                          />
                        </Space>
                      }
                    >
                      <Input
                        placeholder="Enter value"
                        // value={currentData[field.name] || ""}
                        // onChange={(e) =>
                        //   handleInputChange(field.name, e.target.value)
                        // }
                      />
                    </Form.Item>
                  </Col>
                );
              })}
            </>
          </Row>
        </Form>
      );
    } else if (selectedMethod.slug === "gustafson_maples") {
      return (
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <Form.Item
                rules={[{ message: "This field is required", required: true }]}
                label={<Space>Select Combination</Space>}
                hidden
              >
                <Select
                  placeholder="Enter value"
                  value={currentData.combination}
                  onChange={(e) => handleInputChange("combination", e)}
                >
                  {maplesFormFieldsCombinations
                    .filter((item) => {
                      if (selectedTab === 0) {
                        return item.type === "tooth";
                      } else if (selectedTab === 1) {
                        return item.type === "root";
                      } else {
                        return item.type === "tooth_st";
                      }
                    })
                    .map((item, index) => {
                      return (
                        <Select.Option value={item.id} key={index}>
                          {item.name}
                        </Select.Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <>
              {visibleFields.map((code) => {
                const field = johansonGustafsonFormFieldsMapping[code];
                if (!field) return null;

                return (
                  <Col md={8} key={field.name}>
                    <Form.Item
                      name={field.name}
                      rules={[
                        validateNumericValue(field.label),
                        checkAllowedValues(),
                      ]}
                      label={
                        <Space>
                          {field.label}
                          <Button
                            type="text"
                            icon={<InfoCircleOutlined />}
                            onClick={() => showInfoModal(field.name)}
                            style={{ padding: 0, margin: 0 }}
                          />
                        </Space>
                      }
                    >
                      <Input
                        placeholder="Enter value"
                        // value={currentData[field.name] || ""}
                        // onChange={(e) =>
                        //   handleInputChange(field.name, e.target.value)
                        // }
                      />
                    </Form.Item>
                  </Col>
                );
              })}
            </>
          </Row>
        </Form>
      );
    } else if (selectedMethod.slug === "collation") {
      return (
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <Form.Item
                rules={[{ message: "This field is required", required: true }]}
                label={<Space>Select Combination</Space>}
                hidden
              >
                <Select
                  placeholder="Enter value"
                  value={currentData.combination}
                  onChange={(e) => handleInputChange("combination", e)}
                >
                  {collationFormFieldsCombinations
                    .filter((item) => {
                      if (selectedTab === 0) {
                        return item.type === "tooth";
                      } else if (selectedTab === 1) {
                        return item.type === "root";
                      }
                    })
                    .map((item, index) => {
                      return (
                        <Select.Option value={item.id} key={index}>
                          {item.name}
                        </Select.Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <>
              {visibleFields.map((code) => {
                const field = johansonGustafsonFormFieldsMapping[code];
                if (!field) return null;

                return (
                  <Col md={8} key={field.name}>
                    <Form.Item
                      name={field.name}
                      rules={[
                        validateNumericValue(field.label, true),
                        checkAllowedValues(true, field.name),
                      ]}
                      label={
                        <Space>
                          {field.label}
                          <Button
                            type="text"
                            icon={<InfoCircleOutlined />}
                            onClick={() => showInfoModal(field.name)}
                            style={{ padding: 0, margin: 0 }}
                          />
                        </Space>
                      }
                    >
                      <Input
                        placeholder="Enter value"
                        // value={currentData[field.name] || ""}
                        // onChange={(e) =>
                        //   handleInputChange(field.name, e.target.value)
                        // }
                      />
                    </Form.Item>
                  </Col>
                );
              })}
            </>
          </Row>
        </Form>
      );
    }

    return null;
  };

  const handleChangeTab = (num) => {
    if (!selectedTooth || !selectedMethod?.slug) return;
    let defaultCombo = {};

    let comboList =
      selectedMethod?.slug === "johanson_gustafson"
        ? johansonGustafsonFormFieldsCombinations
        : selectedMethod?.slug === "collation"
        ? collationFormFieldsCombinations
        : maplesFormFieldsCombinations;

    defaultCombo = comboList.find(
      (item) =>
        item.type === (num == 0 ? "tooth" : num == 1 ? "root" : "tooth_st")
    );

    handleInputChange("combination", defaultCombo?.id);

    const currentData =
      formData[selectedMethod.slug]?.[selectedTooth.iso] || {};
    if (currentData) {
      form.setFieldsValue(
        formData[selectedMethod.slug][selectedTooth.iso] || {}
      );
    }

    setSelectedTab(num);
  };

  const removeQuadrantPrefix = (name) => {
    return name.replace(/^(Upper|Lower) (Left|Right) /, "");
  };

  // Custom tab items for the preview table

  const getTabItems = () => {
    return selectedMethods
      ?.filter((methodId) => {
        const method = AVAILABLE_METHODS?.find((m) => m.id === methodId);
        return method && Object.keys(formData[method.slug] || {}).length > 0; // ✅ Only keep methods with data
      })
      .map((methodId) => {
        const method = AVAILABLE_METHODS?.find((m) => m.id === methodId);
        return {
          key: methodId.toString(),
          label: `${method.title}`,
          children: (
            <CustomCardWithHeader title={"Preview Selections"}>
              <Table
                dataSource={getTableData(method.slug)}
                columns={getColumns(method.slug)}
                pagination={false}
                locale={{
                  emptyText: (
                    <div style={{ padding: "20px 0", textAlign: "center" }}>
                      No teeth data recorded yet
                    </div>
                  ),
                }}
              />
            </CustomCardWithHeader>
          ),
        };
      });
  };

  const getMethodStatus = (method) => {
    if (!formData.hasOwnProperty(method.slug)) {
      return "none"; // Method slug not found
    }

    const methodTeeth = formData[method.slug];

    // Check if any tooth in this method has at least one scoring field set
    const hasScoringData = Object.values(methodTeeth).some((toothData) =>
      scoringFields.some(
        (field) => toothData[field] !== undefined && toothData[field] !== ""
      )
    );
    return hasScoringData ? "completed" : "none";
  };

  const methodSlugMap = {
    10: "johanson_gustafson",
    9: "gustafson_maples",
    23: "collation",
  };

  const hasAnyNonCollationCompleted = () => {
    return [10, 9].some((id) => {
      const methodSlug = methodSlugMap[id];
      const method = AVAILABLE_METHODS.find((m) => m.slug === methodSlug);
      return method && getMethodStatus(method) === "completed";
    });
  };

  const isCollationCompleted = () => {
    const method = AVAILABLE_METHODS.find((m) => m.id === 23);
    return method && getMethodStatus(method) === "completed";
  };

  const handleMethodToggle = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    methodId = method.id;
    let shouldChangeSelectMethod = true;

    setSelectedMethods((prevSelected) => {
      // --- Handle selecting Collation (23) ---
      if (methodId === 23) {
        if (hasAnyNonCollationCompleted() && !prevSelected.includes(23)) {
          const userConfirmed = window.confirm(
            "Selecting Collation will remove your current progress. Do you want to continue?"
          );
          if (!userConfirmed) {
            shouldChangeSelectMethod = false;
            return prevSelected;
          }

          setFormData({
            johanson_gustafson: {},
            gustafson_maples: {},
            collation: {},
          });
          setSelectedTooth(null);
          return [23];
        }

        return [methodId]; // already selected
      }

      // --- Handle switching away from Collation ---
      if (prevSelected.includes(23)) {
        if (isCollationCompleted()) {
          const userConfirmed = window.confirm(
            "Switching from Collation will remove your current progress. Do you want to continue?"
          );
          if (!userConfirmed) {
            shouldChangeSelectMethod = false;
            return prevSelected;
          }

          setFormData((prev) => ({
            johanson_gustafson: prev.johanson_gustafson || {},
            gustafson_maples: prev.gustafson_maples || {},
            collation: {},
          }));
          setSelectedTooth(null);

          return [methodId];
        }
      }

      // --- Regular add-only logic ---
      if (!prevSelected.includes(methodId) && shouldChangeSelectMethod) {
        return [...prevSelected, methodId];
      }
      setSelectedTooth(null);
      return prevSelected;
    });

    if (shouldChangeSelectMethod) {
      setSelectedMethod(method);

      // Load method data if not already cached
      if (!methodsData[method.slug]) {
        await fetchMethodData(method.slug);
      } else {
        setExamMethodData(methodsData[method.slug]);
      }

      setActiveTableMethod(method);
      setSelectedTooth(null);
    }
  };

  const getIsToothEnabled = (item) => {
    return examMethodData?.teeth?.some((tooth) => tooth.iso == item.iso);
  };

  function isOnlyRootTransparencySet(tooth) {
    return (
      tooth.root_transparency_T &&
      (tooth.attrition_A === undefined || tooth.attrition_A === "") &&
      (tooth.cementum_C === undefined || tooth.cementum_C === "") &&
      (tooth.paradentosis_P === undefined || tooth.paradentosis_P === "") &&
      (tooth.secondary_dentin_S === undefined ||
        tooth.secondary_dentin_S === "")
    );
  }

  const preparePayload = (formData, availableMethods, projectId) => {
    let methodsPayload = [];

    Object.keys(formData).forEach((methodSlug) => {
      const method = availableMethods.find((m) => m.slug === methodSlug);
      if (!method) return;

      // Extract and filter out unsaved teeth
      const teethArray = Object.keys(formData[methodSlug])
        .map((toothKey) => formData[methodSlug][toothKey])
        .filter((toothData) => toothData?.isSaved) // ⛔ remove unsaved teeth
        .map((toothData) => {
          const toothDetails = methodsData[methodSlug]?.teeth?.find(
            (tooth) => tooth.iso === toothData.tooth.iso
          );

          const toothPayload = {
            ...(toothDetails && {
              id: toothDetails.id,
              child: {
                name: toothData.tooth.name,
                fdi: toothData.tooth.iso,
              },
            }),
          };

          // Add other keys except "tooth" and "isSaved"
          Object.keys(toothData).forEach((key) => {
            if (key !== "tooth" && key !== "isSaved") {
              toothPayload[key] = toothData[key];
            }
          });

          // Remove "combination" if method is 9
          if (method.id === 9) {
            if (isOnlyRootTransparencySet(toothPayload)) {
              toothPayload.secondary_dentin_S = "";
            }
            delete toothPayload.combination;
          }

          return toothPayload;
        });

      // Skip if no teeth left after filtering
      if (teethArray.length === 0) return;

      const methodPayload = {
        id: method.id === 23 ? 10 : method.id,
        teeth: teethArray,
      };

      if (method.id === 9) {
        methodPayload.position_known = 1;
      }

      if (method.id === 23) {
        methodPayload.is_collation = 1;
      }

      methodsPayload.push(methodPayload);
    });

    return {
      project_id: projectId,
      methods: methodsPayload,
    };
  };

  const handleProceed = async () => {
    try {
      const payload = preparePayload(
        formData,
        AVAILABLE_METHODS,
        currentCaseDetail?.project_id
      );

      setIsLoading(true);
      const response = await submitAdultsExamResult({ payload: payload });
      if (response.status === 200) {
        setIsLoading(false);
        message.success("Exam result has been submitted successfully");
        navigate(`${ageAssessmentBasePath}/cases/details`, {
          state: { projectId: currentCaseDetail?.project_id },
        });
      }
    } catch (error) {
      setIsLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  const getGuidelinesImage = (type) => {
    const currentGuidelines = methodsData[selectedMethod.slug]?.guidlines || [];

    const foundGuideline = currentGuidelines.find(
      (guideline) =>
        guideline.short_identifier.toLowerCase().replace(/\s+/g, "_") === type
    );

    return foundGuideline ? foundGuideline.image : null;
  };

  const activeBorderStyle = {
    borderBottomWidth: "3px",
    borderBottomStyle: "solid",
    borderBottomColor: "#20c3ff", // Change color dynamically if needed
  };
  const getGuidLinesInformation = () => {
    const currentGuidelines = methodsData[selectedMethod.slug]?.guidlines || [];
    return currentGuidelines;
  };

  const guideLinesArray = getGuidLinesInformation(infoType);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % guideLinesArray.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex - 1 + guideLinesArray.length) % guideLinesArray.length
    );
  };
  const onCancel = () => {
    if (
      Object.keys(formData).some(
        (methodSlug) => Object.keys(formData[methodSlug]).length > 0
      )
    ) {
      const confirmCancel = window.confirm("Are you sure you want to cancel?");
      if (!confirmCancel) {
        return; // Stop if the user cancels
      }
    }
    window.history.back(); // Go back to the previous page
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (imageContainerRef.current?.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };
  const toggleRadioGrapghFullscreen = () => {
    if (!isRadioGraphFullscreen) {
      if (radioGraphContainerRef.current?.requestFullscreen) {
        radioGraphContainerRef.current.requestFullscreen();
        setIsRadioGraphFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsRadioGraphFullscreen(false);
      }
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fcff" }}>
      {/* Breadcrumb */}

      {/* Method Selection */}
      <Card style={{ marginBottom: 24 }}>
        <h4>Method Selection</h4>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Select one or more methods for age assessment.
        </Paragraph>

        <Row gutter={[16, 16]}>
          {AVAILABLE_METHODS.map((method, index) => {
            return (
              <Col md={6} key={index}>
                <Method
                  item={method}
                  selectedMethod={selectedMethod}
                  status={getMethodStatus(method)}
                  onClick={() => handleMethodToggle(method.id)}
                />
              </Col>
            );
          })}
        </Row>
      </Card>

      <div className="tabs_section">
        <ToothOrRadiographTab
          selectdTab={selectedAtiveTab}
          onRadiographClick={() => setSelectedActiveTab(1)}
          onToothClick={() => setSelectedActiveTab(0)}
        />
      </div>

      {/* Tooth Selection */}
      {selectedAtiveTab === 0 ? (
        <>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 0",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Card style={{ marginBottom: 24 }}>
              <Title level={4}>Tooth Analysis</Title>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Select a tooth from the diagram and enter it's corresponding
                values in the designated section.
              </Paragraph>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Row
                  gutter={[10, 10]}
                  className="dental_record_quadrant_section"
                >
                  {[
                    firstQuadrant,
                    secondQuadrant,
                    thirdQuadrant,
                    fourthQuadrant,
                  ].map((quadrant, quadrantIndex) => (
                    <Col md={12} key={quadrantIndex}>
                      <div className="dental_record_quadrant">
                        <Row>
                          {quadrant.map((item, index) => {
                            let isToothEnabled = getIsToothEnabled(item);
                            let isToothActive = selectedTooth?.iso === item.iso;
                            let isDisabled = isToothDisabled(item);
                            return (
                              <Col md={3} key={index}>
                                <div
                                  className="dental_record_singel_tooth"
                                  style={{
                                    background:
                                      !isToothEnabled || isDisabled
                                        ? "#D3D3D3"
                                        : isToothActive
                                        ? "#20c3ff"
                                        : "",
                                    color: isToothActive ? "white" : "",
                                    border: "1px solid grey",
                                    borderRadius: 10,
                                    margin: "0 3px",
                                    height: 80,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                    textAlign: "center",
                                    cursor:
                                      !isToothEnabled || isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      !isToothEnabled || isDisabled ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isToothEnabled || isDisabled) {
                                      return;
                                    }
                                    handleToothClick(item);
                                  }}
                                >
                                  {getToothStatus(item.iso) && (
                                    <img
                                      src={dentalIcons.dentalRecordAdded}
                                      className="dental_record_completed_icon"
                                    />
                                  )}
                                  <p
                                    className="record_number"
                                    style={{
                                      height: 30,
                                      width: 25,
                                      fontWeight: "600",
                                      color: isToothActive ? "white" : "",
                                    }}
                                  >
                                    {item.iso}
                                  </p>
                                  <small>
                                    {removeQuadrantPrefix(item.name)}
                                  </small>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Card>
          )}
        </>
      ) : (
        <div>
          {additional_images.length > 0 ? (
            <PhysicalRadioGraph
              onFullScreen={toggleRadioGrapghFullscreen}
              radioGraphContainerRef={radioGraphContainerRef}
            />
          ) : (
            <Card style={{ marginBottom: 24 }} className="_antCard">
              <div className="readio_graph_not_uploaded">
                No radiographic images uploaded
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Tooth Data Form */}
      {selectedTooth && (
        <CustomCardWithHeader
          title={`${selectedTooth.name} -  ISO ${selectedTooth.iso}`}
        >
          <Card style={{ marginBottom: 24 }}>
            <div className="tooth_data_form_tabs">
              {selectedMethod?.slug !== "gustafson_maples" ? (
                <>
                  <div
                    className="tooth"
                    style={selectedTab === 0 ? activeBorderStyle : {}}
                    onClick={() => handleChangeTab(0)}
                  >
                    <p>Tooth</p>
                  </div>
                  <div
                    className="rooth"
                    style={selectedTab === 1 ? activeBorderStyle : {}}
                    onClick={() => handleChangeTab(1)}
                  >
                    <p>Root</p>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="tooth"
                    style={selectedTab === 0 ? activeBorderStyle : {}}
                    onClick={() => handleChangeTab(0)}
                  >
                    <p>Tooth (APSCT)</p>
                  </div>
                  <div
                    className="tooth"
                    style={selectedTab === 2 ? activeBorderStyle : {}}
                    onClick={() => handleChangeTab(2)}
                  >
                    <p>Tooth (ST)</p>
                  </div>
                  <div
                    className="rooth"
                    style={selectedTab === 1 ? activeBorderStyle : {}}
                    onClick={() => handleChangeTab(1)}
                  >
                    <p>Root (T)</p>
                  </div>
                </>
              )}
            </div>
            {selectedTab === 0 ? (
              <div style={{ padding: "0 24px 24px" }}>
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  Record dental characteristics for the selected tooth
                </Paragraph>

                {renderFormFields()}
                <CustomButtonWithIcon title="Save" onClick={handleSave} />
              </div>
            ) : (
              <div style={{ padding: "0 24px 24px" }}>
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  Record dental characteristics for the selected (Root)
                </Paragraph>

                {renderFormFields()}
                <CustomButtonWithIcon title="Save" onClick={handleSave} />
              </div>
            )}
          </Card>
        </CustomCardWithHeader>
      )}

      {/* Preview Selections */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          Preview Selections
        </Title>

        <Tabs
          activeKey={activeTableMethod.id.toString()}
          onChange={(key) => handleChangeTableMethod(Number.parseInt(key))}
          items={getTabItems()}
        />
      </Card>

      {/* Action Buttons */}

      <Row
        gutter={[16, 16]}
        justify={"space-between"}
        style={{ padding: "0 10px" }}
      >
        <CustomButtonWithIcon
          title="Cancel"
          icon={assessmentIcons.cancel}
          color={"#DC1919"}
          onClick={onCancel}
        />
        <CustomPrimaryButton
          title="Proceed"
          icon={assessmentIcons.proceed}
          disabled={!hasAnyData}
          onClick={handleProceed}
          isLoading={isLoading}
        />
      </Row>

      {/* Info Modal */}
      <Modal
        width={600}
        title={getCustomTitleForGuidlineField(infoType)}
        open={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button key="prev" onClick={handlePrev} disabled={currentIndex === 0}>
            Previous
          </Button>,
          <Button
            key="next"
            onClick={handleNext}
            disabled={currentIndex === guideLinesArray.length - 1}
          >
            Next
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={() => setInfoModalVisible(false)}
          >
            OK
          </Button>,
        ]}
      >
        <div>
          <div style={{ textAlign: "center" }}>
            <img
              src={guideLinesArray[currentIndex]?.image}
              alt={`${guideLinesArray[currentIndex]?.identifier} example`}
              style={{
                maxWidth: "100%",
                borderRadius: "8px",
                marginBottom: 16,
                height: 300,
              }}
            />
            <Title level={5} style={{ marginBottom: 8 }}>
              {guideLinesArray[currentIndex]?.identifier}
            </Title>
            <Paragraph type="secondary">
              {guideLinesArray[currentIndex]?.description}
            </Paragraph>
          </div>
        </div>
      </Modal>
    </div>
  );
}
